/*
 * LEURELA – Produktseiten-Erweiterungen
 * - <lr-live>: aktualisiert sich bei Variantenwechsel aus dem neu gerenderten Section-HTML
 * - <lr-eta>: berechnet ein Lieferfenster in Werktagen
 * - <lr-modal-trigger>: öffnet ein <dialog>
 * - <lr-complete-look>: lädt ergänzende Produkte + Quick-Add in den Warenkorb
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const PRODUCT_SELECT_EVENT = 'shopify:product:select';

class LrLive extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section, dialog');
    this.scope?.addEventListener(PRODUCT_SELECT_EVENT, this.onSelect);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener(PRODUCT_SELECT_EVENT, this.onSelect);
  }

  onSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`lr-live[data-lr-id="${this.dataset.lrId}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

class LrEta extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.min) || 2;
    const max = Number(this.dataset.max) || 5;
    const cutoff = Number(this.dataset.cutoff) || 14;
    const locale = document.documentElement.lang || 'es';
    const start = new Date();
    if (start.getHours() >= cutoff) start.setDate(start.getDate() + 1);

    const addBusinessDays = (date, days) => {
      const d = new Date(date);
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return d;
    };

    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const from = fmt.format(addBusinessDays(start, min));
    const to = fmt.format(addBusinessDays(start, max));
    const template = this.dataset.template || 'Recíbelo entre el {from} y el {to}';
    this.innerHTML = template
      .replace('{from}', `<strong>${from}</strong>`)
      .replace('{to}', `<strong>${to}</strong>`);
  }
}

class LrModalTrigger extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.dialog = this.querySelector('dialog');
    if (!this.button || !this.dialog) return;
    // Dialog an <body> hängen, damit sticky/overflow-Container ihn nicht abschneiden
    document.body.appendChild(this.dialog);
    this.button.addEventListener('click', () => this.dialog.showModal());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-lr-close]')) this.dialog.close();
    });
  }

  disconnectedCallback() {
    this.dialog?.remove();
  }
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'EUR';
  const locale = document.documentElement.lang || 'es';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

async function addToCart(variantId, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const routes = window.Theme?.routes || { cart_add_url: '/cart/add', cart_url: '/cart' };
  try {
    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
    });
    const data = await response.json();
    const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
    const didError = Boolean(data.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'lr-complete-look',
        sourceId: 'lr-complete-look',
        itemCount: 1,
        sections: data.sections,
        didError,
      },
    });
    if (didError) throw new Error(data.description || data.message);
  } catch (error) {
    deferred.reject?.(error);
    throw error;
  }
}

class LrCompleteLook extends HTMLElement {
  async connectedCallback() {
    this.list = this.querySelector('[data-lr-list]');
    this.addEventListener('click', this.onClick);
    if (this.dataset.mode !== 'auto' || !this.list) return;

    const limit = Number(this.dataset.limit) || 3;
    const root = window.Shopify?.routes?.root || '/';
    const load = (intent) =>
      fetch(`${root}recommendations/products.json?product_id=${this.dataset.productId}&limit=${limit}&intent=${intent}`)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((d) => d.products || [])
        .catch(() => []);

    let products = await load('complementary');
    if (!products.length) products = await load('related');
    if (!products.length) {
      this.hidden = true;
      return;
    }
    this.list.innerHTML = products.map((p) => this.renderCard(p)).join('');
  }

  renderCard(product) {
    const image = product.featured_image
      ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=240`
      : '';
    const singleOption = product.options?.length === 1;
    const available = product.variants.filter((v) => v.available);
    const comparePrice = product.compare_at_price > product.price ? `<s>${formatMoney(product.compare_at_price)}</s>` : '';
    let actions = `<a class="lr-look__more" href="${product.url}">${this.dataset.viewLabel || 'Ver producto'}</a>`;

    if (available.length && (singleOption || product.variants.length === 1)) {
      actions = `<div class="lr-look__sizes" role="group" aria-label="${this.dataset.addLabel || 'Añadir'}">${product.variants
        .map(
          (v) =>
            `<button type="button" class="lr-look__size" data-variant-id="${v.id}" ${v.available ? '' : 'disabled'}>${
              product.variants.length === 1 ? '+ ' + (this.dataset.addLabel || 'Añadir') : v.title
            }</button>`
        )
        .join('')}</div>`;
    }

    return `
      <article class="lr-look__card">
        <a class="lr-look__media" href="${product.url}" tabindex="-1">
          ${image ? `<img src="${image}" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy" width="120" height="160">` : ''}
        </a>
        <div class="lr-look__info">
          <a class="lr-look__title" href="${product.url}">${product.title}</a>
          <p class="lr-look__price">${formatMoney(product.price)} ${comparePrice}</p>
          ${actions}
        </div>
      </article>`;
  }

  onClick = async (event) => {
    const button = event.target.closest('[data-variant-id]');
    if (!button || button.disabled) return;
    const original = button.textContent;
    button.classList.add('is-loading');
    button.disabled = true;
    try {
      await addToCart(button.dataset.variantId, button);
      button.classList.add('is-added');
      button.textContent = '✓';
    } catch (error) {
      button.textContent = '×';
    } finally {
      button.classList.remove('is-loading');
      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
        button.classList.remove('is-added');
      }, 1800);
    }
  };
}

// Größen-Schnellkauf auf Produktkarten
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-lr-quick] [data-variant-id]');
  if (!button || button.disabled) return;
  const original = button.textContent;
  button.disabled = true;
  button.classList.add('is-loading');
  try {
    await addToCart(button.dataset.variantId, button);
    button.classList.add('is-added');
    button.textContent = '✓';
  } catch (error) {
    button.textContent = '×';
  } finally {
    button.classList.remove('is-loading');
    setTimeout(() => {
      button.textContent = original;
      button.disabled = false;
      button.classList.remove('is-added');
    }, 1600);
  }
});

// Sanftes Einblenden beim Scrollen
const revealTargets = () => document.querySelectorAll('.lr-reveal:not(.is-visible)');
if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  document.documentElement.classList.add('lr-js-reveal');
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  const observeAll = () => revealTargets().forEach((el) => observer.observe(el));
  observeAll();
  document.addEventListener('shopify:section:load', observeAll);
}

customElements.get('lr-live') || customElements.define('lr-live', LrLive);
customElements.get('lr-eta') || customElements.define('lr-eta', LrEta);
customElements.get('lr-modal-trigger') || customElements.define('lr-modal-trigger', LrModalTrigger);
customElements.get('lr-complete-look') || customElements.define('lr-complete-look', LrCompleteLook);
